import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, unwrap } from './client'
import type { components } from './schema'
import type {
  AdminTenantCreate,
  AggregateType,
  AdminTenantUpdate,
  CriterionInput,
  DepartmentInput,
  FeatureKey,
  FundingAssessmentInput,
  FundingProgramInput,
  MeasureInput,
  MemberInput,
  NewFundingAssessmentInput,
  Quadrant,
  Ratings,
  SchemeKind,
  TenantInput,
  TenantRole,
  TopicInput,
} from './types'

type PlatformAdminInput = components['schemas']['PlatformAdminIn']

/**
 * Data access hooks. Every write invalidates all cached queries: the data set of one
 * municipality is small and scores are derived live, so "refresh everything" is both
 * correct and cheap (a weight change alters every score).
 */
function useWrite<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>, options: { gcTime?: number } = {}) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries(),
    ...options,
  })
}

/**
 * Invitations may carry a one-time initial password: such mutations are dropped from the
 * mutation cache as soon as nobody observes them (gcTime 0), so the password lives only in
 * the component state that displays it.
 */
const SECRET_RESULT = { gcTime: 0 }

/** Filters of the result view. All of them go to the server, so table, CSV export and
 * committee report always show the same set of measures. */
export interface ResultFilter {
  topicId?: string
  department?: string
  quadrant?: Quadrant | ''
  rating?: string // a Rating or 'OPEN'
  withProgram?: boolean
  fiscalYear?: number // Haushaltsjahr
}

const clean = (f: ResultFilter) => ({
  topicId: f.topicId || undefined,
  department: f.department || undefined,
  quadrant: f.quadrant || undefined,
  rating: f.rating || undefined,
  withProgram: f.withProgram || undefined,
  fiscalYear: f.fiscalYear || undefined,
})

// --- session & setup ---------------------------------------------------------------------

export const useMe = () =>
  useQuery({ queryKey: ['me'], queryFn: async () => unwrap(await api.GET('/api/v1/me')), staleTime: 60_000 })

// --- feature toggles (per municipality) ----------------------------------------------------

/** ``enabled`` false outside a municipality (e.g. the developer panel in the platform). */
export const useFeatures = (enabled = true) =>
  useQuery({
    queryKey: ['features'],
    queryFn: async () => unwrap(await api.GET('/api/v1/features')),
    staleTime: 60_000,
    enabled,
  })

/** Whether a feature is switched on for the current municipality (off while loading). */
export function useFeature(key: FeatureKey): boolean {
  const { data } = useFeatures()
  return Boolean(data?.find((f) => f.key === key)?.enabled)
}

export const useSetFeature = () =>
  useWrite(async ({ key, enabled }: { key: FeatureKey; enabled: boolean }) =>
    unwrap(
      await api.PUT('/api/v1/features/{feature}', {
        params: { path: { feature: key } },
        body: { enabled },
      }),
    ),
  )

// --- Übersicht (dashboard) of the municipality ------------------------------------------------

/** Sums of the municipality for its Übersicht (ticket 06); open invitations for the administration. */
export const useDashboard = () =>
  useQuery({ queryKey: ['dashboard'], queryFn: async () => unwrap(await api.GET('/api/v1/dashboard')) })

// --- platform administration ---------------------------------------------------------------

export const useAdminTenants = (enabled = true) =>
  useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: async () => unwrap(await api.GET('/api/v1/admin/tenants')),
    enabled,
  })

/** Figures, Stand and Hinweise of every municipality in one call (platform overview). */
export const usePlatformOverview = (enabled = true) =>
  useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: async () => unwrap(await api.GET('/api/v1/admin/overview')),
    enabled,
  })

/** The functions of any municipality, without opening it (detail page of the platform). */
export const useTenantFeatures = (tenantId: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'features', tenantId],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/admin/tenants/{tenant_id}/features', { params: { path: { tenant_id: tenantId! } } })),
    enabled: Boolean(tenantId),
  })

export const useSetTenantFeature = (tenantId: string) =>
  useWrite(async ({ key, enabled }: { key: FeatureKey; enabled: boolean }) =>
    unwrap(
      await api.PUT('/api/v1/admin/tenants/{tenant_id}/features/{feature}', {
        params: { path: { tenant_id: tenantId, feature: key } },
        body: { enabled },
      }),
    ),
  )

export const useCreateTenant = () =>
  useWrite(async (body: AdminTenantCreate) => unwrap(await api.POST('/api/v1/admin/tenants', { body })))

export const useUpdateTenant = () =>
  useWrite(async ({ id, body }: { id: string; body: AdminTenantUpdate }) =>
    unwrap(await api.PUT('/api/v1/admin/tenants/{tenant_id}', { params: { path: { tenant_id: id } }, body })),
  )

export const useMembers = (tenantId: string | undefined) =>
  useQuery({
    queryKey: ['admin', 'members', tenantId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/admin/tenants/{tenant_id}/members', {
          params: { path: { tenant_id: tenantId! } },
        }),
      ),
    enabled: Boolean(tenantId),
  })

/** Adds a person; with account management the account is created and invited if needed. */
export const useAddMember = (tenantId: string) =>
  useWrite(
    async (body: MemberInput) =>
      unwrap(
        await api.POST('/api/v1/admin/tenants/{tenant_id}/members', {
          params: { path: { tenant_id: tenantId } },
          body,
        }),
      ),
    SECRET_RESULT,
  )

export const useChangeMember = (tenantId: string) =>
  useWrite(async ({ id, role }: { id: string; role: TenantRole }) =>
    unwrap(
      await api.PUT('/api/v1/admin/tenants/{tenant_id}/members/{member_id}', {
        params: { path: { tenant_id: tenantId, member_id: id } },
        body: { role },
      }),
    ),
  )

export const useRemoveMember = (tenantId: string) =>
  useWrite(async (id: string) =>
    unwrap(
      await api.DELETE('/api/v1/admin/tenants/{tenant_id}/members/{member_id}', {
        params: { path: { tenant_id: tenantId, member_id: id } },
      }),
    ),
  )

/** Whether login accounts and the platform administration are managed in the application. */
export const useIdentityStatus = (enabled = true) =>
  useQuery({
    queryKey: ['admin', 'identity'],
    queryFn: async () => unwrap(await api.GET('/api/v1/admin/identity')),
    enabled,
    staleTime: 5 * 60_000,
  })

export const usePlatformAdmins = (enabled = true) =>
  useQuery({
    queryKey: ['admin', 'platform-admins'],
    queryFn: async () => unwrap(await api.GET('/api/v1/admin/platform-admins')),
    enabled,
  })

export const useGrantPlatformAdmin = () =>
  useWrite(
    async (body: PlatformAdminInput) => unwrap(await api.POST('/api/v1/admin/platform-admins', { body })),
    SECRET_RESULT,
  )

export const useRevokePlatformAdmin = () =>
  useWrite(async (accountId: string) =>
    unwrap(
      await api.DELETE('/api/v1/admin/platform-admins/{account_id}', {
        params: { path: { account_id: accountId } },
      }),
    ),
  )

export const useTenant = (enabled = true) =>
  useQuery({ queryKey: ['tenant'], queryFn: async () => unwrap(await api.GET('/api/v1/tenant')), enabled })

export const useSaveTenant = () =>
  useWrite(async (body: TenantInput) => unwrap(await api.PUT('/api/v1/tenant', { body })))

export const useTopics = () =>
  useQuery({ queryKey: ['topics'], queryFn: async () => unwrap(await api.GET('/api/v1/topics')) })

export const useSaveTopic = () =>
  useWrite(async ({ id, body }: { id?: string; body: TopicInput }) =>
    id
      ? unwrap(await api.PUT('/api/v1/topics/{topic_id}', { params: { path: { topic_id: id } }, body }))
      : unwrap(await api.POST('/api/v1/topics', { body })),
  )

export const useDeleteTopic = () =>
  useWrite(async (id: string) =>
    unwrap(await api.DELETE('/api/v1/topics/{topic_id}', { params: { path: { topic_id: id } } })),
  )

// --- departments (Fachamt-Katalog) --------------------------------------------------------

/** The Fachamt-Katalog in catalog order (sort order, then name), with usage per department. */
export const useDepartments = () =>
  useQuery({ queryKey: ['departments'], queryFn: async () => unwrap(await api.GET('/api/v1/departments')) })

export const useSaveDepartment = () =>
  useWrite(async ({ id, body }: { id?: string; body: DepartmentInput }) =>
    id
      ? unwrap(
          await api.PUT('/api/v1/departments/{department_id}', { params: { path: { department_id: id } }, body }),
        )
      : unwrap(await api.POST('/api/v1/departments', { body })),
  )

export const useDeleteDepartment = () =>
  useWrite(async (id: string) =>
    unwrap(await api.DELETE('/api/v1/departments/{department_id}', { params: { path: { department_id: id } } })),
  )

/** Moves all measures of ``id`` to ``targetId`` and deletes ``id``. */
export const useMergeDepartment = () =>
  useWrite(async ({ id, targetId }: { id: string; targetId: string }) =>
    unwrap(
      await api.POST('/api/v1/departments/{department_id}/merge', {
        params: { path: { department_id: id } },
        body: { targetId },
      }),
    ),
  )

// --- schemes -----------------------------------------------------------------------------

export const useSchemes = () =>
  useQuery({ queryKey: ['schemes'], queryFn: async () => unwrap(await api.GET('/api/v1/schemes')) })

export const useScheme = (kind: SchemeKind) =>
  useQuery({
    queryKey: ['schemes', kind],
    queryFn: async () => unwrap(await api.GET('/api/v1/schemes/{kind}', { params: { path: { kind } } })),
  })

export const useSaveScheme = (kind: SchemeKind) =>
  useWrite(async ({ criteria, version }: { criteria: CriterionInput[]; version?: number }) =>
    unwrap(
      await api.PUT('/api/v1/schemes/{kind}', { params: { path: { kind } }, body: { criteria, version } }),
    ),
  )

/** Live score from the server – same calculation as the results (no client-side maths). */
export const useEvaluation = (kind: SchemeKind, ratings: Ratings, enabled = true) =>
  useQuery({
    queryKey: ['evaluate', kind, ratings],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/schemes/{kind}/evaluate', {
          params: { path: { kind } },
          body: { ratings },
        }),
      ),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  })

// --- measures ----------------------------------------------------------------------------

export const useMeasures = () =>
  useQuery({ queryKey: ['measures'], queryFn: async () => unwrap(await api.GET('/api/v1/measures')) })

export const useMeasure = (id: string | undefined) =>
  useQuery({
    queryKey: ['measures', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/measures/{measure_id}', { params: { path: { measure_id: id! } } })),
    enabled: Boolean(id),
  })

export const useSaveMeasure = () =>
  useWrite(async ({ id, body }: { id?: string; body: MeasureInput }) =>
    id
      ? unwrap(await api.PUT('/api/v1/measures/{measure_id}', { params: { path: { measure_id: id } }, body }))
      : unwrap(await api.POST('/api/v1/measures', { body })),
  )

export const useDeleteMeasure = () =>
  useWrite(async (id: string) =>
    unwrap(await api.DELETE('/api/v1/measures/{measure_id}', { params: { path: { measure_id: id } } })),
  )

// --- funding programs --------------------------------------------------------------------

export const usePrograms = () =>
  useQuery({
    queryKey: ['programs'],
    queryFn: async () => unwrap(await api.GET('/api/v1/funding-programs')),
  })

export const useProgram = (id: string | undefined) =>
  useQuery({
    queryKey: ['programs', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/funding-programs/{program_id}', { params: { path: { program_id: id! } } }),
      ),
    enabled: Boolean(id),
  })

export const useSaveProgram = () =>
  useWrite(async ({ id, body }: { id?: string; body: FundingProgramInput }) =>
    id
      ? unwrap(
          await api.PUT('/api/v1/funding-programs/{program_id}', {
            params: { path: { program_id: id } },
            body,
          }),
        )
      : unwrap(await api.POST('/api/v1/funding-programs', { body })),
  )

export const useDeleteProgram = () =>
  useWrite(async (id: string) =>
    unwrap(
      await api.DELETE('/api/v1/funding-programs/{program_id}', { params: { path: { program_id: id } } }),
    ),
  )

// --- funding assessments / program comparison --------------------------------------------

export const useComparison = (measureId: string | undefined) =>
  useQuery({
    queryKey: ['comparison', measureId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/measures/{measure_id}/funding-assessments', {
          params: { path: { measure_id: measureId! } },
        }),
      ),
    enabled: Boolean(measureId),
  })

export const useCreateAssessment = (measureId: string) =>
  useWrite(async (body: NewFundingAssessmentInput) =>
    unwrap(
      await api.POST('/api/v1/measures/{measure_id}/funding-assessments', {
        params: { path: { measure_id: measureId } },
        body,
      }),
    ),
  )

export const useUpdateAssessment = () =>
  useWrite(async ({ id, body }: { id: string; body: FundingAssessmentInput }) =>
    unwrap(
      await api.PUT('/api/v1/funding-assessments/{assessment_id}', {
        params: { path: { assessment_id: id } },
        body,
      }),
    ),
  )

export const useDeleteAssessment = () =>
  useWrite(async (id: string) =>
    unwrap(
      await api.DELETE('/api/v1/funding-assessments/{assessment_id}', {
        params: { path: { assessment_id: id } },
      }),
    ),
  )

// --- results & report --------------------------------------------------------------------

export const useResults = (filter: ResultFilter) =>
  useQuery({
    queryKey: ['results', clean(filter)],
    queryFn: async () => unwrap(await api.GET('/api/v1/results', { params: { query: clean(filter) } })),
    placeholderData: (previous) => previous,
  })

export const useCommitteeReport = (filter: ResultFilter, includeUnsuitable: boolean) =>
  useQuery({
    queryKey: ['report', clean(filter), includeUnsuitable],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/reports/committee', {
          params: { query: { ...clean(filter), includeUnsuitable } },
        }),
      ),
  })

/** Query string of a result filter (for the CSV export and the committee report URL). */
export function filterQuery(filter: ResultFilter): URLSearchParams {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(clean(filter))) {
    if (value !== undefined) params.set(key, String(value))
  }
  return params
}

export function csvExportPath(filter: ResultFilter): string {
  const query = filterQuery(filter).toString()
  return `/api/v1/results/export.csv${query ? `?${query}` : ''}`
}

// --- change log (Änderungsprotokoll) ----------------------------------------------------------

export interface ChangeFilter {
  aggregateType?: AggregateType
  aggregateId?: string
}

const CHANGES_PAGE = 50

/** The municipality's change log, newest first; ``fetchNextPage`` loads older entries. */
export const useChanges = (filter: ChangeFilter) =>
  useInfiniteQuery({
    queryKey: ['changes', filter.aggregateType ?? null, filter.aggregateId ?? null],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/changes', {
          params: {
            query: {
              aggregateType: filter.aggregateType,
              aggregateId: filter.aggregateId,
              limit: CHANGES_PAGE,
              before: pageParam,
            },
          },
        }),
      ),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    // a new filter keeps the old list until the new one is there – no spinner, no height jump
    placeholderData: keepPreviousData,
  })

/** The latest change of one object (null: none recorded yet). */
export const useLastChange = (aggregateType: AggregateType, aggregateId: string | undefined) =>
  useQuery({
    queryKey: ['changes', 'last', aggregateType, aggregateId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/changes', {
          params: { query: { aggregateType, aggregateId: aggregateId!, limit: 1 } },
        }),
      ).items[0] ?? null,
    enabled: Boolean(aggregateId),
  })
